import { useLayoutEffect, useState } from "react";

import { resolveRenderedSurfaces } from "./cardSurfaces";
import { Color } from "./themes/color";

/** WCAG 2.1 non-text contrast (1.4.11) — icons are graphics, not body text. */
const WCAG_AA_NON_TEXT = 3;

const THEME_FOREGROUND_CLASS = "text-card-foreground";

/**
 * Resolves the class an authored `icon_color` actually renders as, measuring the class instead of
 * mapping Tailwind names to hex so custom and arbitrary classes are covered too.
 *
 * Authored colors were picked against the legacy light card, so on a dark palette some fall below
 * the non-text contrast bar; those degrade to the theme foreground rather than disappearing into
 * the card. Fails open — when the color can't be measured (jsdom, unparseable computed value) the
 * authored class is kept, so intentional styling wins over a guess.
 */
export const useReadableIconColorClass = (
  iconColor: string | null | undefined,
  /** Element whose surface the icon sits on; also the realm the probe is measured in. */
  cardRef: React.RefObject<HTMLElement | null>,
  /**
   * Changes whenever the card surface may have — the resolved theme, or a render that painted the
   * card — so the icon is re-measured against what the card shows now.
   */
  surfaceSignature: string
): string => {
  const [isUnreadable, setIsUnreadable] = useState(false);

  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!iconColor || !card) {
      setIsUnreadable(false);
      return;
    }
    // Measured on a throwaway probe rather than the live icon: the icon may already be showing the
    // fallback, which would measure as readable and flip the decision back every render.
    const probe = card.ownerDocument.createElement("span");
    probe.className = iconColor;
    probe.style.cssText = "position:absolute;visibility:hidden;pointer-events:none";
    card.appendChild(probe);
    const view = card.ownerDocument.defaultView ?? window;
    const foreground = Color.tryParse(view.getComputedStyle(probe).color);
    card.removeChild(probe);

    setIsUnreadable(!isIconColorReadable(foreground, resolveRenderedSurfaces(card)));
  }, [iconColor, cardRef, surfaceSignature]);

  if (!iconColor || isUnreadable) {
    return THEME_FOREGROUND_CLASS;
  }
  return iconColor;
};

/**
 * Readable when it clears the non-text bar against every surface the icon can sit on — a card
 * painted with a gradient has more than one, and passing only the friendliest end would let an
 * icon vanish over the rest of it.
 *
 * Deliberately readable when nothing is measurable, so an unresolvable color keeps its authored
 * class instead of being overridden blind.
 */
export const isIconColorReadable = (
  foreground: Color | null | undefined,
  surfaces: readonly Color[]
): boolean => {
  if (!foreground || surfaces.length === 0) {
    return true;
  }
  return surfaces.every(surface => foreground.contrastRatio(surface) >= WCAG_AA_NON_TEXT);
};
